/**
 * BodyTypeGuidanceModal - Extracted from App.js Phase 3
 * Shows personalized nutrition guidance based on user's somatotype
 */
import React from 'react';
import { AITransparencyNotice } from '../ui/UtilityComponents';

const BodyTypeGuidanceModal = ({ 
  isOpen, 
  onClose, 
  userProfile, 
  bodyTypeGuidance, 
  allBodyTypeGuidance 
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <h2 className="heading-3">Body Type Nutrition Guide</h2>
        <p className="modal-description">Personalized nutrition guidance based on your somatotype</p>
        <div className="premium-badge">Premium Feature</div>

        {/* User's Current Body Type Card */}
        {userProfile?.body_composition?.body_type && bodyTypeGuidance && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid rgba(102, 126, 234, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '32px' }}>💪</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>
                  Your Body Type: {bodyTypeGuidance.type || userProfile?.body_composition?.body_type}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {bodyTypeGuidance.overview}
                </p>
              </div>
            </div>
            
            {/* Macro Ratio Pills */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={{ background: 'rgba(76, 175, 80, 0.2)', padding: '10px 16px', borderRadius: '8px', textAlign: 'center', flex: 1, minWidth: '80px' }}>
                <p style={{ margin: 0, fontSize: '11px', color: '#4caf50' }}>PROTEIN</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '20px', fontWeight: '700', color: '#4caf50' }}>
                  {bodyTypeGuidance.macro_split?.protein || 30}%
                </p>
              </div>
              <div style={{ background: 'rgba(255, 152, 0, 0.2)', padding: '10px 16px', borderRadius: '8px', textAlign: 'center', flex: 1, minWidth: '80px' }}>
                <p style={{ margin: 0, fontSize: '11px', color: '#ff9800' }}>CARBS</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '20px', fontWeight: '700', color: '#ff9800' }}>
                  {bodyTypeGuidance.macro_split?.carbs || 40}%
                </p>
              </div>
              <div style={{ background: 'rgba(33, 150, 243, 0.2)', padding: '10px 16px', borderRadius: '8px', textAlign: 'center', flex: 1, minWidth: '80px' }}>
                <p style={{ margin: 0, fontSize: '11px', color: '#2196f3' }}>FAT</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '20px', fontWeight: '700', color: '#2196f3' }}>
                  {bodyTypeGuidance.macro_split?.fat || 30}%
                </p>
              </div>
            </div>

            {/* Calorie Adjustment & Meal Frequency */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Calorie Adjustment</p>
                <p style={{ 
                  margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600',
                  color: bodyTypeGuidance.guidance?.calorie_adjustment > 0 ? '#4caf50' : 
                         bodyTypeGuidance.guidance?.calorie_adjustment < 0 ? '#f44336' : 'var(--text-primary)'
                }}>
                  {bodyTypeGuidance.guidance?.calorie_adjustment > 0 ? '+' : ''}
                  {bodyTypeGuidance.guidance?.calorie_adjustment} kcal
                </p>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Meal Frequency</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {bodyTypeGuidance.guidance?.meal_frequency}
                </p>
              </div>
            </div>

            {/* Tips */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                Tips for {bodyTypeGuidance.body_type}:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {bodyTypeGuidance.guidance?.tips?.slice(0, 4).map((tip, idx) => (
                  <li key={idx} style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', lineHeight: '1.4' }}>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* Foods to Emphasize/Limit */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#4caf50' }}>
                  Foods to Emphasize
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {bodyTypeGuidance.guidance?.foods_to_emphasize?.slice(0, 6).map((food, idx) => (
                    <span key={idx} style={{ fontSize: '11px', background: 'rgba(76, 175, 80, 0.15)', padding: '4px 8px', borderRadius: '4px', color: '#4caf50' }}>
                      {food}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#f44336' }}>
                  Foods to Limit
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {bodyTypeGuidance.guidance?.foods_to_limit?.slice(0, 4).map((food, idx) => (
                    <span key={idx} style={{ fontSize: '11px', background: 'rgba(244, 67, 54, 0.15)', padding: '4px 8px', borderRadius: '4px', color: '#f44336' }}>
                      {food}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All Body Types Section */}
        {allBodyTypeGuidance && (
          <div>
            <h3 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
              {userProfile?.body_composition?.body_type ? 'Other Body Types' : 'All Body Types'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '300px', overflowY: 'auto' }}>
              {Object.entries(allBodyTypeGuidance)
                .filter(([type]) => !userProfile?.body_composition?.body_type || type !== bodyTypeGuidance?.body_type)
                .map(([type, guidance]) => (
                <div key={type} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)' }}>{type}</h4>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <span style={{ fontSize: '10px', background: 'rgba(76, 175, 80, 0.2)', padding: '2px 6px', borderRadius: '4px', color: '#4caf50' }}>P: {guidance.macro_ratio.protein}%</span>
                      <span style={{ fontSize: '10px', background: 'rgba(255, 152, 0, 0.2)', padding: '2px 6px', borderRadius: '4px', color: '#ff9800' }}>C: {guidance.macro_ratio.carbs}%</span>
                      <span style={{ fontSize: '10px', background: 'rgba(33, 150, 243, 0.2)', padding: '2px 6px', borderRadius: '4px', color: '#2196f3' }}>F: {guidance.macro_ratio.fat}%</span>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>{guidance.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <AITransparencyNotice />

        <button className="btn-secondary full-width" onClick={onClose} style={{ marginTop: '16px' }}>
          Close
        </button>
      </div>
    </div>
  );
};

export default BodyTypeGuidanceModal;
